import { type UIAlertProps, PlayerAccountClient } from '../types'
import type { UserStatusGuard } from '../types/composables/UseGuard'
import type { RouteLocationNormalizedGeneric } from '#vue-router'
import { toValue, navigateTo, useNotifications, useDayjs, useUserAuthClient, useCoreConfig, useUserInfo, useNuxtApp, ACCOUNT_RESTRICTION_STATUSES } from '#imports'

interface CheckResultContext {
    ends_at?: string
    redirectUrl?: string
    [key: string]: unknown
}

export const useGuard = () => {
    const userInfoQuery = useUserInfo({
        lazy: false,
        server: true
    })
    const { data, isLoggedIn } = userInfoQuery
    const { getLoginLimitsCheck } = useUserAuthClient()
    const { guards } = useCoreConfig()
    const dayjs = useDayjs()
    const { add: addNotification } = useNotifications()
    const { $i18n } = useNuxtApp()

    const handleNotification = (alert: UIAlertProps, status?: PlayerAccountClient.components['schemas']['UserInfoResponse']['checkResult']) => {
        if (!status) return

        let ends_at: string | undefined = undefined
        const context = status.context as unknown as CheckResultContext | undefined

        switch (status.type) {
            case PlayerAccountClient.UserCheckResultType.block:
                ends_at = context?.ends_at
                break

            case PlayerAccountClient.UserCheckResultType.cooldown:
                ends_at = context?.ends_at
                break
        }

        if (ends_at) {
            const formattedEndtime = dayjs(ends_at).tz().format('DD/MM/YYYY HH:mm:ss')
            const alertMessage = alert?.message?.replace('{date}', formattedEndtime)

            addNotification({
                type: status.type,
                alert: {
                    ...alert,
                    message: alertMessage,
                },
            })

            return
        }

        addNotification({
            type: status.type,
            alert,
        })
    }

    const resolveStatus = async () => {
        const statusRaw = toValue(data.value?.checkResult)

        if (statusRaw) return statusRaw

        try {
            const { data: loginLimitsData } = await getLoginLimitsCheck()

            if (loginLimitsData.value?.data?.withinLimits === false) {
                return {
                    type: 'reached-play-limits' as PlayerAccountClient.UserCheckResultType,
                    context: {
                        redirectUrl: '/users/reached-play-limits'
                    }
                }
            }
        }
        catch { /* fail open */ }

        return
    }

    type ResolvedStatus = NonNullable<Awaited<ReturnType<typeof resolveStatus>>>

    const applyStatusRedirect = async (
        statusRaw: ResolvedStatus,
        statusObject: UserStatusGuard,
        route?: RouteLocationNormalizedGeneric
    ) => {
        const redirectTarget = statusObject.route
        const targetPath = (redirectTarget as RouteLocationNormalizedGeneric)?.name || redirectTarget

        // Some statuses must land on a server-rendered page: the SumSub KYC page
        // needs the CSP nonce, which only exists on the server. Those opt in with
        // `external`, forcing a full document navigation instead of a client-side
        // route change so the target is re-rendered by the server.
        const redirect = () => statusObject.external
            ? navigateTo(redirectTarget, { external: true })
            : navigateTo(redirectTarget)

        // Already on the correct page, so we allow the navigation to complete.
        if (route?.fullPath === targetPath || route?.name === targetPath) return true

        if (statusObject.alert) {
            handleNotification(statusObject.alert, statusRaw as PlayerAccountClient.components['schemas']['UserInfoResponse']['checkResult'])
        }

        if (statusObject.isForcedRedirect) {
            if (route) return redirect()

            await redirect()

            return false
        }

        if (route && guards.extraAllowedRoutes.includes(route.name)) return true

        if (route) return redirect()

        await redirect()

        return false
    }

    const can = async (route?: RouteLocationNormalizedGeneric) => {
        if (!isLoggedIn.value) return true

        if (route) {
            const matchingStatusKeys = Object.entries(guards.statuses)
                .filter(([, statusGuard]) => {
                    if (statusGuard.allowWithoutStatus) return false

                    const targetPath = (statusGuard.route as RouteLocationNormalizedGeneric)?.name || statusGuard.route

                    return route.fullPath === targetPath || route.name === targetPath
                })
                .map(([key]) => key)

            if (matchingStatusKeys.length > 0) {
                const status = await resolveStatus()

                if (status?.type && matchingStatusKeys.includes(status.type)) return true

                if (status?.type) {
                    const statusObject = guards.statuses[status.type as PlayerAccountClient.UserCheckResultType]

                    if (statusObject) return applyStatusRedirect(status, statusObject, route)
                }

                const frontpage = $i18n.t('pages.lobby')

                if (route.fullPath === frontpage || route.name === frontpage) return true

                return navigateTo(frontpage)
            }
        }

        // Allow visiting allowed routes regardless of status. These are the
        // safe-harbor routes a restricted (e.g. deactivated) user is deliberately
        // permitted to reach.
        if (route && guards.allowedRoutes.includes(route.name)) return true

        const statusRaw = await resolveStatus()

        if (!statusRaw) return true

        const statusObject = statusRaw?.type ? guards.statuses?.[statusRaw.type] : undefined

        if (!statusObject) {
            // Fail safe: the backend reported a critical account restriction that
            // the app forgot to configure. Failing open here would silently expose
            // sensitive pages (e.g. deposit). Warn loudly and redirect to the
            // frontpage, unless we're already somewhere safe.
            if (statusRaw?.type && ACCOUNT_RESTRICTION_STATUSES.has(statusRaw.type)) {
                console.warn(
                    `[useGuard] User has critical status "${statusRaw.type}" but it is not configured in guards.statuses. `
                    + 'Redirecting to the frontpage as a fail-safe — add this status to guards.statuses.'
                )

                const frontpage = $i18n.t('pages.lobby')

                if (route?.fullPath === frontpage || route?.name === frontpage) return true

                if (route) return navigateTo(frontpage)

                await navigateTo(frontpage)

                return false
            }

            return true
        }

        return applyStatusRedirect(statusRaw, statusObject, route)
    }

    return userInfoQuery.enrich({
        can
    })
}
